import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Phone } from "lucide-react";
import BrandLogo from "./BrandLogo";
import RingMeNowButton from "./RingMeNowButton";
import LanguageSwitcher from "./LanguageSwitcher";

/**
 * Page-level brand header sitting above every page:
 *   LEFT  → Janté logo + red tagline (matches the "Travel Concierges"
 *           wordmark beneath the logo in font + scale, in red).
 *   RIGHT → Language switcher pill + "Live concierge" button + callback link
 */
export default function PageHeader() {
  const { t } = useTranslation();
  return (
    <div
      className="w-full border-b border-[var(--emerald)]/5 bg-[var(--ivory)]"
      data-testid="page-header"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between gap-6">
        {/* Left: logo + red mission tagline (styled to match "Concierges" in the wordmark) */}
        <div className="flex items-center gap-6 min-w-0">
          <Link
            to="/"
            className="flex items-center shrink-0"
            aria-label="Janté Travel Concierges — home"
            data-testid="page-header-logo"
          >
            <BrandLogo className="h-32 sm:h-40" />
          </Link>
          <Link
            to="/tenant-thrive"
            className="hidden md:block text-[var(--brand-red)] hover:opacity-80 transition-opacity italic"
            style={{
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: "1.35rem",
              fontWeight: 500,
              letterSpacing: "0.04em",
              lineHeight: 1.15,
            }}
            data-testid="header-mission-tagline"
          >
            Vacation With The Mission In Mind
          </Link>
        </div>

        {/* Right: language switcher + live concierge */}
        <div className="flex items-center gap-3 shrink-0">
          <LanguageSwitcher />
          <div className="hidden sm:flex flex-col items-stretch gap-1">
            <a
              href="tel:+14454552683"
              className="inline-flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.22em] px-4 py-2.5 bg-[var(--brand-red)] text-[var(--ivory)] hover:opacity-90 transition-opacity"
              data-testid="header-live-concierge"
            >
              <Phone size={12} strokeWidth={1.5} /> {t("concierge.live")} · +1 445 45Jante (52683)
            </a>
            <a
              href="https://scheduler.zoom.us/jante-luxury-travel/membershipenrollmentreview"
              target="_blank"
              rel="noopener noreferrer"
              className="text-center text-[10px] uppercase tracking-[0.2em] px-4 py-1.5 text-[var(--emerald)] hover:text-[var(--brand-red)] transition-colors"
              data-testid="header-schedule-callback"
            >
              {t("concierge.scheduleCallback")}
            </a>
            <div className="mt-1 flex justify-center">
              <RingMeNowButton compact />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
